import { LogEvent } from '@/modules/core/amplitude/types/Amplitude';
import { hasValue } from '@/modules/core/data/record';
import { CustomerTheme } from '@/modules/core/data/types/CustomerTheme';
import { EducationalProgramStep } from '@/modules/core/data/types/EducationalProgramStep';
import { GenericObject } from '@/modules/core/data/types/GenericObject';
import { shouldDisplayStepTuitionFees } from '@/modules/solutions/educationalProgramStep';
import { Amplitude } from '@amplitude/react-amplitude';
import {
  css,
  useTheme,
} from '@emotion/react';
import filter from 'lodash.filter';
import isEqual from 'lodash.isequal';
import some from 'lodash.some';
import React, {
  Fragment,
  useEffect,
  useState,
} from 'react';
import TuitionFeeButton from './TuitionFeeButton';
import TuitionFeeContainer from './TuitionFeeContainer';

type Props = {
  selectedEducationalProgramSteps: Array<EducationalProgramStep>;
}

const EducationalProgramStepFees: React.FunctionComponent<Props> = (props): JSX.Element => {
  const {
    selectedEducationalProgramSteps,
  }: Props = props;
  const selectedEducationalProgramStepsWithTuitionFees = filter(selectedEducationalProgramSteps, shouldDisplayStepTuitionFees);
  const theme: CustomerTheme = useTheme();
  const { primaryColor } = theme;
  const [isDisplayingFees, setIsDisplayingFees] = useState<boolean>(false);
  const [educationalProgramSteps, setEducationalProgramSteps] = useState<EducationalProgramStep[]>(selectedEducationalProgramStepsWithTuitionFees);
  const [activeEducationalProgramStep, setActiveEducationalProgramStep] = useState<EducationalProgramStep>(selectedEducationalProgramStepsWithTuitionFees?.[0]);

  /**
   * Sync state (from older props) with newer props.
   *
   * XXX This ensures that we keep displaying up-to-date information when the user selects different program steps in the ContextBuilder
   */
  useEffect(() => {
    if (!isEqual(educationalProgramSteps, selectedEducationalProgramStepsWithTuitionFees)) {
      setEducationalProgramSteps(selectedEducationalProgramStepsWithTuitionFees);
      setActiveEducationalProgramStep(selectedEducationalProgramStepsWithTuitionFees?.[0]);
    }
  }, [educationalProgramSteps, selectedEducationalProgramStepsWithTuitionFees]);

  const hasAnyTuitionFeesDescription: boolean = some(educationalProgramSteps, (educationalProgramStep: EducationalProgramStep) => {
    return hasValue(educationalProgramStep, 'tuitionFeesDescription');
  });

  if (!activeEducationalProgramStep || !hasAnyTuitionFeesDescription) {
    return null;
  }

  return (
    <Amplitude
      eventProperties={(inheritedProps): GenericObject => ({
        ...inheritedProps,
      })}
    >
      {({ logEvent }: { logEvent: LogEvent }): JSX.Element => (
        <div
          css={css`
            margin: 10px 0px 10px 0px;

            b {
              color: ${primaryColor};
            }
          `}
        >
          {
            !isDisplayingFees ? (
              // If we aren't displaying the fees (user hasn't clicked yet), then display the spoiler button,
              // but only if there are some fees to be shown in any step (otherwise it'd be pointless to display a CTA that won't show anything useful)
              <TuitionFeeButton
                setIsDisplayingFees={setIsDisplayingFees}
                logEvent={logEvent}
                activeEducationalProgramStep={activeEducationalProgramStep}
                hasAnyTuitionFeesDescription={hasAnyTuitionFeesDescription}
              />
            ) : (
              <Fragment>
                <TuitionFeeContainer
                  activeEducationalProgramStep={activeEducationalProgramStep}
                  selectedEducationalProgramStepsWithTuitionFees={selectedEducationalProgramStepsWithTuitionFees}
                  setActiveEducationalProgramStep={setActiveEducationalProgramStep}
                  setIsDisplayingFees={setIsDisplayingFees}
                />
              </Fragment>
            )
          }
        </div>
      )}
    </Amplitude>
  );
};

export default EducationalProgramStepFees;
